import type { Message } from '../state';
import type { LifecycleSnapshot } from './snapshot';
import {
  getDurationSeconds,
  hasVisualization,
  readFeedbackSignatures,
  withTraceId,
} from './tracker.helpers';
import { SIDEKICK_LIFECYCLE_EVENT, type SidekickLifecycleEvent } from './types';

/** Follows Sidekick state and reports lifecycle events as it changes. */
export type LifecycleTracker = {
  /** Reports what changed since the last snapshot. */
  sync: (next: LifecycleSnapshot) => void;
  /** Ends the open session and reports nothing until resumed. */
  pause: () => void;
  /** Tracks again from the given state, starting a session if one is due. */
  resume: (next: LifecycleSnapshot) => void;
};

type Session = {
  conversationId: string;
  startedAtMs: number;
  messageCount: number;
};

type PendingResponse = {
  message: Message;
  conversationId: string;
  latencyMs?: number;
  timeoutId?: ReturnType<typeof setTimeout>;
};

/** Wait for late attachments to an answer that arrives outside a stream. */
export const RESPONSE_FLUSH_DELAY_MS = 100;

/**
 * Turns successive Sidekick snapshots into lifecycle events. A session
 * runs while the panel is open on a loaded conversation; messages and
 * feedback count only inside one, and what it starts with is history.
 * A retry sends its question again, so it counts as another query.
 * An answer is reported once its turn ends, when its trace id and any
 * late visualization are on it.
 * @param initial - State when tracking begins; a panel already open on a
 *   conversation starts a session without a panel-open event.
 * @param emit - Receives each event.
 * @returns The tracker.
 */
export const createLifecycleTracker = (
  initial: LifecycleSnapshot,
  emit: (event: SidekickLifecycleEvent) => void
): LifecycleTracker => {
  let previous = initial;
  let session: Session | null = null;
  let seenMessageIds = new Set<string>();
  let seenFeedback: ReadonlyMap<string, string> = new Map();
  let lastQueryAtMs: number | null = null;
  let isPaused = false;
  const pendingResponses = new Map<string, PendingResponse>();

  const flushResponse = (messageId: string, turnTraceId?: string): void => {
    const pending = pendingResponses.get(messageId);
    if (pending === undefined) return;
    clearTimeout(pending.timeoutId);
    pendingResponses.delete(messageId);
    seenMessageIds.add(messageId);
    emit({
      type: SIDEKICK_LIFECYCLE_EVENT.RESPONSE_RECEIVED,
      conversationId: pending.conversationId,
      ...withTraceId(pending.message.traceId || turnTraceId),
      hasVisualization: hasVisualization(pending.message),
      ...(pending.latencyMs !== undefined
        ? { latencyMs: pending.latencyMs }
        : {}),
    });
  };

  // The chat puts a turn's one trace on its last answer; the rest share it.
  const flushAllResponses = (): void => {
    const turnTraceId = [...pendingResponses.values()].reduce(
      (traceId, { message }) => message.traceId || traceId,
      ''
    );
    for (const messageId of [...pendingResponses.keys()]) {
      flushResponse(messageId, turnTraceId);
    }
  };

  const dropPendingResponses = (): void => {
    for (const pending of pendingResponses.values()) {
      clearTimeout(pending.timeoutId);
    }
    pendingResponses.clear();
  };

  const endSession = (): void => {
    if (session === null) return;
    flushAllResponses();
    emit({
      type: SIDEKICK_LIFECYCLE_EVENT.SESSION_ENDED,
      conversationId: session.conversationId,
      messageCount: session.messageCount,
      durationSeconds: getDurationSeconds(session.startedAtMs),
    });
    session = null;
  };

  const startSession = (
    snapshot: LifecycleSnapshot,
    conversationId: string
  ): void => {
    dropPendingResponses();
    seenMessageIds = new Set(snapshot.messages.map(({ id }) => id));
    seenFeedback = readFeedbackSignatures(snapshot.submittedFeedback);
    lastQueryAtMs = null;
    session = {
      conversationId,
      startedAtMs: Date.now(),
      messageCount: snapshot.messages.length,
    };
    emit({ type: SIDEKICK_LIFECYCLE_EVENT.SESSION_STARTED, conversationId });
  };

  const syncSession = (next: LifecycleSnapshot): void => {
    if (!previous.isPanelOpen && next.isPanelOpen) {
      emit({
        type: SIDEKICK_LIFECYCLE_EVENT.PANEL_OPENED,
        conversationId: next.conversationId,
      });
    }
    if (
      (previous.isPanelOpen && !next.isPanelOpen) ||
      (previous.conversationId !== null && next.conversationId === null)
    ) {
      endSession();
    }
    const { conversationId } = next;
    if (
      next.isPanelOpen &&
      conversationId !== null &&
      next.hasConversation &&
      session?.conversationId !== conversationId
    ) {
      endSession();
      startSession(next, conversationId);
    }
  };

  const queueResponse = (
    message: Message,
    conversationId: string,
    isStreaming: boolean
  ): void => {
    pendingResponses.set(message.id, {
      message,
      conversationId,
      latencyMs:
        lastQueryAtMs === null ? undefined : Date.now() - lastQueryAtMs,
      timeoutId: isStreaming
        ? undefined
        : setTimeout(() => flushResponse(message.id), RESPONSE_FLUSH_DELAY_MS),
    });
  };

  const reportQuery = (question: Message, conversationId: string): void => {
    lastQueryAtMs = Date.now();
    emit({
      type: SIDEKICK_LIFECYCLE_EVENT.QUERY_SUBMITTED,
      conversationId,
      inputLength: question.content.length,
    });
  };

  const syncMessages = (next: LifecycleSnapshot): void => {
    if (session === null) return;
    session.messageCount = Math.max(session.messageCount, next.messages.length);
    if (next.messages !== previous.messages) {
      for (const message of next.messages) {
        const pending = pendingResponses.get(message.id);
        if (pending !== undefined) {
          pending.message = message;
        } else if (!seenMessageIds.has(message.id)) {
          if (message.role === 'assistant') {
            queueResponse(message, session.conversationId, next.isStreaming);
          } else {
            seenMessageIds.add(message.id);
            if (message.role === 'user') {
              reportQuery(message, session.conversationId);
            }
          }
        }
      }
    }
    const { retryingQuestion } = next;
    if (
      retryingQuestion !== null &&
      retryingQuestion.id !== previous.retryingQuestion?.id
    ) {
      reportQuery(retryingQuestion, session.conversationId);
    }
    if (previous.isStreaming && !next.isStreaming) {
      flushAllResponses();
    }
  };

  const syncFeedback = (next: LifecycleSnapshot): void => {
    if (session === null) return;
    if (next.submittedFeedback === previous.submittedFeedback) return;
    const seen = seenFeedback;
    // A cleared rating is forgotten, since picking it again sends it again.
    seenFeedback = readFeedbackSignatures(next.submittedFeedback);
    for (const [messageId, signature] of seenFeedback) {
      if (seen.get(messageId) === signature) continue;
      const rated = next.messages.find(({ id }) => id === messageId);
      emit({
        type: SIDEKICK_LIFECYCLE_EVENT.FEEDBACK_SUBMITTED,
        // An answer kept from before an edit belongs to its own
        // conversation, the one its feedback is filed under.
        conversationId: rated?.conversationId || session.conversationId,
        ...withTraceId(rated?.traceId),
        thumbsUp: next.submittedFeedback[messageId]?.rating === 'up',
      });
    }
  };

  const sync = (next: LifecycleSnapshot): void => {
    if (!isPaused) {
      syncSession(next);
      syncMessages(next);
      syncFeedback(next);
    }
    previous = next;
  };

  sync(initial);

  return {
    sync,
    pause: () => {
      endSession();
      dropPendingResponses();
      isPaused = true;
    },
    resume: (next) => {
      isPaused = false;
      sync(next);
    },
  };
};
